import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { AuditTrailForExport, getAuditTrailData } from "../util/getAuditTrailData";
import { uploadJsonToS3 } from "../util/AppUtils";

const baseUrl = BASE_URL + "category";

// 📌 Create Category (POST)
export const createCategory = createAsyncThunk(
  "category/create",
  async (categoryData, { rejectWithValue }) => {
    try {
      const version_content = {
        ...categoryData,
        action_to_show: `Created Category ${categoryData.category_name}`,
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "category_created",
        "CREATE",
        true
      );

      const audit_trail_data = await getAuditTrailData({
        action: "CREATE_CATEGORY",
        comment: `Created Category ${categoryData.category_name}`,
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const response = await axios.post(`${baseUrl}/create`, {
        ...categoryData,
        audit_trail_data,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating category:", error);
      return rejectWithValue(error.response?.data || "Error creating category");
    }
  }
);

// 📌 Update Category (PUT)
export const updateCategory = createAsyncThunk(
  "category/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const version_content = {
        ...data,
        action_to_show: "Edit Category Pop-Up Table",
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "category_updated",
        "UPDATE",
        true
      );

      const audit_trail_data = await getAuditTrailData({
        action: "UPDATE_CATEGORY",
        comment: `Updated Category ${data.category_name}`,
        parent_version:data.version_id_ref,
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const payload = { ...data, audit_trail_data };

      const response = await axios.put(`${baseUrl}/update/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating category");
    }
  }
);

// 📌 Get Category by ID (GET)
export const getCategoryById = createAsyncThunk(
  "category/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching category");
    }
  }
);

// 📌 Get All Categories (GET)
export const getAllCategories = createAsyncThunk(
  "category/getAll",
  async ({ limit = 100, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching categories"
      );
    }
  }
);

export const fetchAllCategories = createAsyncThunk(
  "category/fetchAll",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?${searchQuery}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching categories"
      );
    }
  }
);

// 📌 Delete Category (DELETE)
export const deleteCategory = createAsyncThunk(
  "category/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting category");
    }
  }
);

// 📌 Upload CSV (POST)
export const uploadCSV = createAsyncThunk(
  "category/uploadCSV",
  async ({ file, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("audit_trail_data", JSON.stringify(auditTrailData));

      const response = await axios.post(`${baseUrl}/add-upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error uploading CSV");
    }
  }
);

// 📌 Update via CSV (POST)
export const updateCSV = createAsyncThunk(
  "category/updateCSV",
  async ({ csvFile, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("csvfile", csvFile);
      formData.append("audit_trail_data", JSON.stringify(auditTrailData));

      const response = await axios.post(`${baseUrl}/upt-upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating CSV");
    }
  }
);

// 📌 Export CSV (GET)
export const exportCSV = createAsyncThunk(
  "category/exportCSV",
  async (_, { rejectWithValue }) => {
    try {    

      await AuditTrailForExport({
        apiUrl:`${baseUrl}/export-csv`,
        file_name:"category-data-"+Date.now()+".csv",
        action:"EXPORT_CATEGORY_DATA",
        action_to_show:"Category Table data exported as CSV"
      })
      return [];

    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);

// 🛠 Category Slice
const categorySlice = createSlice({
  name: "category",
  initialState: {
    categories: [],
    category: null,
    loading: false,
    error: null,
  },
  reducers: {}, // No reducers needed as we handle async actions
  extraReducers: (builder) => {
    builder
      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        // state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      // Update Category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.map((cat) =>
          cat.id === action.payload.id ? action.payload : cat
        );
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      // Get Category by ID
      .addCase(getCategoryById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.category = action.payload;
      })
      .addCase(getCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      // Get All Categories
      .addCase(fetchAllCategories.pending, (state) => {
        state.loading = true;
        // state.error = null;
      })
      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.results;
        state.pagination = action.payload.pagination; // Store pagination data
        state.error = null;
      })
      .addCase(fetchAllCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })


      // Fetch All Categories
      .addCase(getAllCategories.pending, (state) => {
        state.loading = true;
        // state.error = null;
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.results;
        state.pagination = action.payload.pagination; // Store pagination data
        state.error = null;
      })
      .addCase(getAllCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      // Delete Category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter(
          (cat) => cat.id !== action.payload
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.error || action.payload.message || "An error occurred";
      })

      // Upload CSV
      .addCase(uploadCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update via CSV
      .addCase(updateCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export CSV
      .addCase(exportCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default categorySlice.reducer;
